import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowUpRightIcon, PlusIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { serviceGroups } from '../data/services';
const ease = [0.23, 1, 0.32, 1] as const;
type Group = (typeof serviceGroups)[number];
export function ServicesAccordion() {
  const [open, setOpen] = useState<number | null>(null);

  // Split into two independent columns so opening a row never stretches its neighbour
  const half = Math.ceil(serviceGroups.length / 2);
  const columns = [serviceGroups.slice(0, half), serviceGroups.slice(half)];
  return <section id="services" aria-labelledby="services-heading" className="relative bg-bg px-page pb-20 pt-12 md:pb-28 md:pt-14" data-id="element-320" data-mp="ib8scpfv55ui">
      {/* Same quiet label style as "Trusted by ambitious brands" */}
      <div className="flex items-baseline gap-3" data-id="element-321" data-mp="1tn9kmebxrcf0">
        <h2 id="services-heading" className="text-[12px] font-medium uppercase tracking-[0.04em] text-fg/60" data-id="element-322" data-mp="1po0qv9gwbejg">
          What we do
        </h2>
        <span className="text-[12px] font-medium uppercase tracking-[0.04em] text-fg/40" aria-hidden="true" data-id="element-323" data-mp="7a4ttjzk1src">
          ({String(serviceGroups.length).padStart(2, '0')})
        </span>
      </div>

      {/* Short about paragraph on the left; the service list starts on the same line, on the right */}
      <div className="mt-4 flex flex-col gap-10 md:mt-5 lg:flex-row lg:items-start lg:justify-between lg:gap-[var(--gutter)]" data-id="element-324" data-mp="l9e9mijumkus">
        <p className="max-w-[340px] text-[15px] leading-[1.55] text-fg/60" data-id="element-325" data-mp="3oqw4lpeivxds">
          LAEBEL is the AI-native brand venture studio of KAYS Culture. Not an agency, not a service provider: one
          source that builds, operates and scales through ownership and equity.
        </p>

      {/* Right column: the two service lists, then a CTA aligned to the same left edge */}
      <div className="w-full lg:w-[54%] lg:min-w-[580px]" data-id="element-326" data-mp="3111vnixbqsyt">
      <div className="grid w-full gap-x-10 md:grid-cols-2 md:items-start" data-id="element-327" data-mp="35zmtjbp3t0k3">
        {columns.map((col, c) => <ul key={c} className={`border-line ${c === 0 ? 'border-t' : 'md:border-t'}`} data-id="element-328" data-mp="d4whav2y6wmg" data-mpi={c}>
            {col.map((group, j) => {
              const index = c * half + j;
              return <ServiceRow key={group.name} group={group} index={index} isOpen={open === index} onToggle={() => setOpen(open === index ? null : index)} data-id="element-329" data-mp="lx6r4b9xl036" data-mpi={j} />;
            })}
          </ul>)}
      </div>

      {/* Button stacked above the supporting line, both left-aligned with the list */}
      <div className="mt-10 flex flex-col items-start gap-5 md:mt-14 md:gap-6" data-id="element-330" data-mp="38jpy3uvkuuv">
        <Link to="/contact" className="group inline-flex h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-control border border-fg/30 px-5 text-[13px] uppercase tracking-[0.04em] text-fg transition-colors duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] hover:border-fg hover:bg-fg hover:text-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-fg focus-visible:ring-offset-2" data-id="element-331" data-mp="3ud078p0co9jp">
          Start a project
          <ArrowUpRightIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-332" data-mp="1m43hzaoev9an" />
        </Link>
        <p className="text-[15px] leading-[1.5] text-fg/60" data-id="element-333" data-mp="20wdoi4w0gbdf">
          No hourly rates. <span className="text-fg" data-id="element-334" data-mp="25uxcsby2tz8y">Fixed scopes or equity.</span>
        </p>
      </div>
      </div>
      </div>
    </section>;
}
type RowProps = {
  group: Group;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
};
function ServiceRow({
  group,
  index,
  isOpen,
  onToggle
}: RowProps) {
  const reduce = useReducedMotion();
  const panelId = `service-panel-${index}`;
  return <li className="border-b border-line" data-id="element-335" data-mp="2okn5mtia4ay8">
      <button type="button" onClick={onToggle} aria-expanded={isOpen} aria-controls={panelId} className="group flex w-full items-center justify-between gap-4 py-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-fg md:py-[14px]" data-id="element-336" data-mp="2olosdv8nqlvs">
        <span className="flex items-baseline gap-4" data-id="element-337" data-mp="qz75kj7qy4yb">
          <span className="w-7 shrink-0 text-[12px] tabular-nums text-fg/35" data-id="element-338" data-mp="lzb4arqformg">
            {String(index + 1).padStart(2, '0')}
          </span>
          <span className={`text-[18px] font-normal leading-[1.3] tracking-[-0.01em] transition-colors duration-150 md:text-[21px] ${isOpen ? 'text-fg' : 'text-fg/75 group-hover:text-fg'}`} data-id="element-339" data-mp="1jlrxplw1ql2x">
            {group.name}
          </span>
        </span>
        <PlusIcon aria-hidden="true" className={`h-4 w-4 shrink-0 transition-[transform,color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] ${isOpen ? 'rotate-45 text-fg' : 'text-fg/45 group-hover:text-fg'}`} data-id="element-340" data-mp="14xdvnd6gf0nf" />
      </button>

      <AnimatePresence initial={false} data-id="element-341" data-mp="24ysiyu5rafqv">
        {isOpen && <motion.div id={panelId} key="panel" initial={reduce ? {
        opacity: 0
      } : {
        height: 0,
        opacity: 0
      }} animate={reduce ? {
        opacity: 1
      } : {
        height: 'auto',
        opacity: 1
      }} exit={reduce ? {
        opacity: 0
      } : {
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease
      }} className="overflow-hidden" data-id="element-342" data-mp="250d8u6h4laie">
            <p className="max-w-[420px] pb-5 pl-11 pr-8 text-[15px] leading-[1.55] text-fg/60" data-id="element-343" data-mp="2nv0n1un18kkl">
              {group.description}
            </p>
          </motion.div>}
      </AnimatePresence>
    </li>;
}